<template>
  <div data-testid="field-mapping-trail-row" class="field-mapping-trail-row">
    <!--
      G2 — Collapsed summary row. Always visible. Renders meaning
      (count + first explanation) without leaking individual field
      names. Click toggles the entire expanded panel via `v-if`,
      not `v-show`, so the per-field table is not in the DOM until
      the dealer asks for it (G4).
    -->
    <button
      type="button"
      class="summary-toggle"
      :aria-expanded="expanded"
      :aria-controls="panelId"
      data-testid="field-mapping-trail-summary-toggle"
      @click="expanded = !expanded"
    >
      <span class="summary-text">
        <span
          class="summary-primary"
          data-testid="field-mapping-trail-summary-primary"
        >
          {{ summaryPrimary }}
        </span>
        <span
          v-if="summarySecondary.length > 0"
          class="summary-secondary"
          :title="summarySecondary"
          data-testid="field-mapping-trail-summary-secondary"
        >
          {{ summarySecondary }}
        </span>
      </span>
      <v-icon
        :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        size="small"
        class="summary-chevron"
      />
    </button>

    <!--
      G4 — Hard lazy render. The entire expanded panel is gated by
      `v-if`; toggling `expanded` mounts/unmounts it from the DOM.
      `v-show` is intentionally NOT used so the unrendered panel
      does not contribute any field-name text to the DOM.
    -->
    <div
      v-if="expanded"
      :id="panelId"
      class="expanded-panel mt-2"
      data-testid="field-mapping-trail-expanded-panel"
    >
      <!--
        G5 — Friendly empty state. When defensive filtering removes
        every field-change entry, render a single tonal alert and
        skip the banner / table / "+N more" entirely. Avoids the
        "headers above an empty table" UX failure.
      -->
      <v-alert
        v-if="fieldChanges.length === 0"
        type="info"
        variant="tonal"
        density="comfortable"
        data-testid="field-mapping-trail-empty"
      >
        No field changes captured for this vehicle.
      </v-alert>

      <template v-else>
        <!--
          G2/G3 — priorReadFailed banner sits ABOVE the table inside
          the expanded panel only. Suppressed in the empty-state
          branch (the empty-state alert takes precedence so the
          dealer doesn't see two competing messages).
        -->
        <div
          v-if="priorReadFailed"
          class="text-caption text-medium-emphasis mb-2"
          data-testid="field-mapping-trail-prior-read-failed-banner"
        >
          {{ priorReadFailedCopy }}
        </div>

        <!--
          G3 — Per-field table. Columns: Field / Before / After /
          Source / Explanation. Values are truncated client-side
          with `:title` exposing the full string for hover.
        -->
        <v-table
          density="compact"
          class="field-changes-table"
          data-testid="field-mapping-trail-table"
        >
          <thead>
            <tr>
              <th>Field</th>
              <th>Before</th>
              <th>After</th>
              <th>Source</th>
              <th>Explanation</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(change, idx) in visibleFieldChanges"
              :key="`change:${idx}:${change.field}`"
              :data-testid="`field-mapping-trail-row-change-${idx}`"
            >
              <td :title="change.field" class="field-cell">
                {{ truncate(change.field, FIELD_NAME_TRUNCATE_LIMIT) }}
              </td>
              <td :title="change.beforeTitle" class="value-cell">
                {{ change.beforeDisplay }}
              </td>
              <td :title="change.afterTitle" class="value-cell">
                {{ change.afterDisplay }}
              </td>
              <td :title="change.source" class="source-cell">
                {{ change.source || EMPTY_VALUE_SENTINEL }}
              </td>
              <td :title="change.explanation" class="explanation-cell">
                {{ change.explanation || EMPTY_VALUE_SENTINEL }}
              </td>
            </tr>
          </tbody>
        </v-table>

        <!--
          G4 — within-expanded "+N more" toggle. Hard-gated by `v-if`
          on `expandedShowAll`: the overflow rows themselves are
          NEVER rendered until the dealer explicitly opts in. The
          test surface verifies absence, not visibility.
        -->
        <button
          v-if="!expandedShowAll && overflowCount > 0"
          type="button"
          class="collapse-toggle"
          data-testid="field-mapping-trail-show-more"
          @click="expandedShowAll = true"
        >
          {{ showMoreCopy }}
        </button>
        <button
          v-if="expandedShowAll && overflowCount > 0"
          type="button"
          class="collapse-toggle"
          data-testid="field-mapping-trail-show-less"
          @click="expandedShowAll = false"
        >
          Show less
        </button>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Phase 8.y — FieldMappingDecisionTrailRow
 *
 * Renders one fieldMapping decision record inside the VIN-trail
 * dialog. Pure presentational — no store reads, no fetches, no
 * navigation. Mirrors `GroupsDecisionTrailRow.vue` for the
 * presentational invariants but implements the option-C UX
 * (compact summary + lazy expander) because fieldMapping records
 * can carry dozens of field changes per VIN.
 *
 * Guardrails (see Phase 8.y plan):
 *   - G2: collapsed summary surfaces meaning ("N fields updated"
 *     plus the first non-empty `reasons[i].explanation` truncated
 *     on a single line); no individual field names in the
 *     collapsed view.
 *   - G3: strict Before/After rendering rules with em-dash sentinel
 *     for missing values, client-side truncation, and `title`
 *     tooltip exposing full text. A missing `afterValue` (writer
 *     contract violation) renders the sentinel AND emits a one-shot
 *     console.warn for diagnostic visibility.
 *   - G4: hard lazy render. The expanded panel uses `v-if` (not
 *     `v-show`); rows beyond `FIELD_CHANGES_COLLAPSE_THRESHOLD` are
 *     ALSO hard-gated by `v-if` so they are not in the DOM until
 *     the user clicks "+N more".
 *   - G5: zero valid field-changes -> friendly empty alert; never
 *     render an empty headered table.
 *   - priorReadFailed banner sits INSIDE the expanded panel above
 *     the table; suppressed under the empty-state branch.
 */
import { computed, ref } from 'vue'
import {
  publishFlowFieldMappingPriorReadFailedCopy,
  publishFlowFieldMappingFieldsUpdatedCopy,
  publishFlowFieldMappingFieldsCollapseCopy,
  FIELD_CHANGES_COLLAPSE_THRESHOLD,
  FIELD_NAME_TRUNCATE_LIMIT,
  VALUE_TRUNCATE_LIMIT,
  EMPTY_VALUE_SENTINEL,
} from '@/utils/publishFlowStageLabels'
import type {
  PublishDecisionRecord,
  PublishDecisionReason,
} from '@/services/publishPreviewArtifactsService'

interface FieldChangeView {
  field: string
  beforeDisplay: string
  afterDisplay: string
  beforeTitle: string
  afterTitle: string
  source: string
  explanation: string
}

const props = defineProps<{
  record: PublishDecisionRecord
}>()

// Stable ID for the `aria-controls` -> panel relationship. Math.random
// is fine here because a row instance is short-lived and we never
// persist this id across renders.
const panelId = `field-mapping-trail-panel-${Math.random().toString(36).slice(2, 9)}`

const expanded = ref(false)
const expandedShowAll = ref(false)
// One-shot guard so a malformed record (`afterValue === undefined`)
// emits exactly one diagnostic warn per row instance regardless of
// how many such rows exist or how many times the panel is expanded.
const missingAfterWarnEmitted = ref(false)

const priorReadFailed = computed(
  () => props.record.provenance?.priorReadFailed === true,
)

const priorReadFailedCopy = computed(() => publishFlowFieldMappingPriorReadFailedCopy())

/**
 * Defensive view-model construction. The persisted Phase 8 record
 * carries per-field `{ field, beforeValue?, afterValue, sourceAfter?,
 * explanation }` entries on `record.reasons[]`. Entries without a
 * usable `field` string are filtered out (G3); writer order is
 * preserved.
 */
const fieldChanges = computed<FieldChangeView[]>(() => {
  const raw = props.record.reasons
  if (!Array.isArray(raw)) return []
  const out: FieldChangeView[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const field = (entry as PublishDecisionReason).field
    if (typeof field !== 'string' || field.length === 0) continue
    out.push(buildFieldChangeView(entry as PublishDecisionReason))
  }
  return out
})

/**
 * G2 secondary line. Scans the ORIGINAL `reasons[]` (not the
 * post-`field`-filter list) so an entry that lacks `field` but has
 * a usable explanation still contributes to the collapsed-summary
 * hint. Returns the trimmed first non-empty explanation; empty
 * string when none qualifies.
 */
const summarySecondary = computed<string>(() => {
  const raw = props.record.reasons
  if (!Array.isArray(raw)) return ''
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const explanation = (entry as PublishDecisionReason).explanation
    if (typeof explanation !== 'string') continue
    const trimmed = explanation.trim()
    if (trimmed.length > 0) return trimmed
  }
  return ''
})

const summaryPrimary = computed(() =>
  publishFlowFieldMappingFieldsUpdatedCopy(fieldChanges.value.length),
)

const overflowCount = computed(() =>
  Math.max(0, fieldChanges.value.length - FIELD_CHANGES_COLLAPSE_THRESHOLD),
)

const showMoreCopy = computed(() =>
  publishFlowFieldMappingFieldsCollapseCopy(overflowCount.value),
)

/**
 * G4 — Visible slice. Returns the full list when `expandedShowAll`
 * is true; otherwise the first `FIELD_CHANGES_COLLAPSE_THRESHOLD`
 * entries. Used by the table v-for; the unrendered tail is NEVER
 * placed in the DOM (verified by the row's tests).
 */
const visibleFieldChanges = computed<FieldChangeView[]>(() =>
  expandedShowAll.value
    ? fieldChanges.value
    : fieldChanges.value.slice(0, FIELD_CHANGES_COLLAPSE_THRESHOLD),
)

/**
 * Coerce an arbitrary `unknown` to a display string. The writer
 * normalizes most values upstream, but the contract permits
 * non-string `beforeValue` / `afterValue` (numbers, nulls, etc.),
 * so the row stays defensive. Stringification matches `String()`
 * semantics for primitives; objects fall back to JSON which is
 * truncated downstream.
 */
function coerceDisplay(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return ''
  }
}

function truncate(value: string, limit: number): string {
  if (typeof value !== 'string') return ''
  if (value.length <= limit) return value
  return `${value.slice(0, limit - 1)}\u2026`
}

/**
 * Build the per-row view-model. G3 rules:
 *   - `beforeValue === undefined` -> Before cell renders sentinel,
 *     `:title` shows nothing useful (sentinel as title also).
 *   - `afterValue === undefined` -> After cell renders sentinel AND
 *     a one-shot console.warn fires (writer contract violation).
 *   - Long values truncated for display, original kept in `:title`.
 */
function buildFieldChangeView(entry: PublishDecisionReason): FieldChangeView {
  const beforeRaw = entry.beforeValue
  const afterRaw = entry.afterValue
  const sourceRaw = typeof entry.sourceAfter === 'string' ? entry.sourceAfter : ''
  const explanationRaw = typeof entry.explanation === 'string' ? entry.explanation : ''

  const beforeDisplay =
    beforeRaw === undefined
      ? EMPTY_VALUE_SENTINEL
      : truncate(coerceDisplay(beforeRaw), VALUE_TRUNCATE_LIMIT)
  const beforeTitle =
    beforeRaw === undefined ? EMPTY_VALUE_SENTINEL : coerceDisplay(beforeRaw)

  let afterDisplay: string
  let afterTitle: string
  if (afterRaw === undefined) {
    if (!missingAfterWarnEmitted.value) {
      missingAfterWarnEmitted.value = true
      console.warn(
        JSON.stringify({
          event: 'publishFlow.fieldMappingTrailRow.missingAfterValue',
          vin: props.record.vin,
          field: entry.field,
        }),
      )
    }
    afterDisplay = EMPTY_VALUE_SENTINEL
    afterTitle = EMPTY_VALUE_SENTINEL
  } else {
    afterDisplay = truncate(coerceDisplay(afterRaw), VALUE_TRUNCATE_LIMIT)
    afterTitle = coerceDisplay(afterRaw)
  }

  return {
    field: entry.field || '',
    beforeDisplay,
    afterDisplay,
    beforeTitle,
    afterTitle,
    source: sourceRaw,
    explanation: explanationRaw,
  }
}
</script>

<style scoped>
.field-mapping-trail-row {
  /* The parent v-list-item already supplies vertical rhythm. */
}
.summary-toggle {
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
}
.summary-text {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}
.summary-primary {
  font-weight: 500;
  font-size: 13px;
}
.summary-secondary {
  display: block;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin-top: 2px;
  max-width: 100%;
}
.summary-chevron {
  flex: 0 0 auto;
}
.expanded-panel {
  /* Tight padding so the table sits cleanly under the summary row. */
}
.field-changes-table {
  font-size: 12px;
}
.field-changes-table .field-cell {
  font-family:
    'JetBrains Mono', 'SFMono-Regular', Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
  font-size: 11px;
  white-space: nowrap;
}
.field-changes-table .value-cell {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.field-changes-table .source-cell {
  white-space: nowrap;
}
.field-changes-table .explanation-cell {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.collapse-toggle {
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  margin-top: 6px;
  color: #1d4ed8;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}
.collapse-toggle:hover {
  text-decoration: underline;
}
</style>
